import api from './api';

const tradeSettingsService = {
  /**
   * Get all trade settings (tags, systems, setups)
   */
  async getAllSettings({ includeArchived = false } = {}) {
    const response = await api.get('/trade-settings', {
      params: includeArchived ? { include_archived: 'true' } : {}
    });
    return response.data;
  },

  /**
   * Get settings by type
   * @param {string} type - 'tag', 'system', or 'setup'
   */
  async getSettingsByType(type) {
    const response = await api.get(`/trade-settings/${type}`);
    return response.data;
  },

  /**
   * Create a new setting
   * @param {string} type - 'tag', 'system', or 'setup'
   * @param {string} value - The setting value
   * @param {object} options - Optional description and color
   */
  async createSetting(type, value, options = {}) {
    const response = await api.post('/trade-settings', {
      type,
      value,
      ...options
    });
    return response.data;
  },

  /**
   * Update a setting
   */
  async updateSetting(id, updates) {
    const response = await api.put(`/trade-settings/${id}`, updates);
    return response.data;
  },

  /**
   * Delete a setting
   */
  async deleteSetting(id) {
    const response = await api.delete(`/trade-settings/${id}`);
    return response.data;
  },

  async getDeleteInfo(id) {
    const response = await api.get(`/trade-settings/${id}/delete-info`);
    return response.data;
  },

  async restoreSetting(id) {
    const response = await api.post(`/trade-settings/${id}/restore`);
    return response.data;
  },

  /**
   * Reorder settings
   */
  async reorderSettings(type, orderedIds) {
    const response = await api.post('/trade-settings/reorder', {
      type,
      orderedIds
    });
    return response.data;
  },

  /**
   * Seed default settings
   */
  async seedDefaults() {
    const response = await api.post('/trade-settings/seed-defaults');
    return response.data;
  },

  // ============================================
  // Custom Tags Configuration (entry/exit tags)
  // ============================================

  /**
   * Get all tag configurations (entry and exit tags)
   * Returns: { entry: { tag_1: {...}, tag_2: {...}, tag_3: {...} }, exit: { tag_1: {...}, ... } }
   */
  async getTagsConfig() {
    const response = await api.get('/trade-settings/tags-config');
    return response.data;
  },

  /**
   * Update a single tag configuration
   * @param {string} tagType - 'entry' or 'exit'
   * @param {number} tagNumber - 1, 2, or 3
   * @param {string} label - Display label for the tag
   * @param {string[]} options - Array of options
   * @param {boolean} allowCustom - Allow custom values
   */
  async updateTagConfig(tagType, tagNumber, label, options, allowCustom = false) {
    const response = await api.put('/trade-settings/tags-config', {
      tag_type: tagType,
      tag_number: tagNumber,
      label,
      options,
      allow_custom: allowCustom
    });
    return response.data;
  },

  /**
   * Archive a tag option (smart delete: hard delete if unused, archive if used)
   */
  async archiveTagOption(tagType, tagNumber, value) {
    const response = await api.post('/trade-settings/tags-config/archive-option', {
      tag_type: tagType,
      tag_number: tagNumber,
      value
    });
    return response.data;
  },

  /**
   * Restore an archived tag option
   */
  async restoreTagOption(tagType, tagNumber, value) {
    const response = await api.post('/trade-settings/tags-config/restore-option', {
      tag_type: tagType,
      tag_number: tagNumber,
      value
    });
    return response.data;
  },

  /**
   * Seed default tag configurations
   */
  async seedDefaultTagsConfig() {
    const response = await api.post('/trade-settings/tags-config/seed-defaults');
    return response.data;
  },

  // ============================================
  // Item 86 Part D follow-up — retroactive R backfill
  // ============================================

  /**
   * Count trades with no R that a backfill would target.
   * @param {object} opts - { scope: 'system'|'global', system?: string }
   */
  async previewRiskBackfill(opts) {
    const response = await api.post('/trade-settings/risk-backfill/preview', opts);
    return response.data;
  },

  /**
   * Apply the default risk → R to trades that currently have no R (fills blanks
   * only; never touches a real/broker stop or an existing default).
   * @param {object} opts - { scope: 'system'|'global', system?: string }
   */
  async runRiskBackfill(opts) {
    const response = await api.post('/trade-settings/risk-backfill', opts);
    return response.data;
  }
};

export default tradeSettingsService;
